import { Global } from "@emotion/react";
import { DropdownMenu, Pill } from "@fullscript/aviary-web";
import { faCheck } from "@fullscript/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { regionLoginHref, type LoginRegion } from "@shared/helpers/regionLoginHref/regionLoginHref";
import { useHydrated } from "@shared/hooks/useHydrated/useHydrated";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { isDevelopment, isProduction } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import * as styles from "./CountrySelect.styles";

const REGION_FLAG: Record<LoginRegion, string> = {
  us: "🇺🇸",
  ca: "🇨🇦",
  nova: "🌐",
};

const ALL_REGIONS: LoginRegion[] = ["nova", "us", "ca"];

const isLoginRegion = (value: string): value is LoginRegion =>
  ALL_REGIONS.includes(value as LoginRegion);

// In dev the backend config doesn't vary per subdomain, so the subdomain — not the
// backend props — decides which region is current.
const regionFromLocalHostname = (hostname: string): LoginRegion | null => {
  const subdomain = hostname.split(".")[0];

  return isLoginRegion(subdomain) ? subdomain : null;
};

interface Props {
  slug?: string;
  /** For pages outside GlobalContext (NOVA's /n/login); defaults to the backend config. */
  currentRegion?: LoginRegion;
}

const CountrySelect = ({ slug, currentRegion: currentRegionProp }: Props) => {
  const { isCanada, isMultiRegion } = useSharedGlobalConfig();
  const { t } = useTranslation();
  const hydrated = useHydrated();

  // NOVA's login page can't see the backend's isMultiRegion flag (it renders outside
  // GlobalContext), but NOVA only exists in multi-region deployments.
  const isMultiRegionDeployment = isMultiRegion || currentRegionProp === "nova";
  const backendRegion = currentRegionProp ?? (isCanada ? "ca" : "us");

  const regionName: Record<LoginRegion, string> = {
    us: t(l.regionSwitch.us),
    ca: t(l.regionSwitch.ca),
    nova: t(l.regionSwitch.nova),
  };

  const regionLoginPageLabel: Record<LoginRegion, string> = {
    us: t(l.regionSwitch.usLoginPage),
    ca: t(l.regionSwitch.caLoginPage),
    nova: t(l.regionSwitch.novaLoginPage),
  };

  const goToRegionLabel: Record<LoginRegion, string> = {
    us: t(l.regionSwitch.goToUS),
    ca: t(l.regionSwitch.goToCA),
    nova: t(l.regionSwitch.goToNova),
  };

  // NOVA's /n/login is server-rendered, where reading window.location would crash.
  if (!hydrated) return null;

  const currentRegion = isDevelopment()
    ? (regionFromLocalHostname(window.location.hostname) ?? backendRegion)
    : backendRegion;

  const regionLabel = (region: LoginRegion) => `${REGION_FLAG[region]} ${regionName[region]}`;

  const inProduction = isProduction();

  // The picker is an internal affordance; multi-region production gets no switcher.
  if (inProduction && isMultiRegionDeployment) return null;

  const canSwitchRegion = inProduction || isDevelopment() || isMultiRegionDeployment;

  if (!canSwitchRegion) return <Pill>{regionLabel(currentRegion)}</Pill>;

  // Production regions live on their own domain, so NOVA is never reachable from one.
  const regions: LoginRegion[] = inProduction
    ? [currentRegion, isCanada ? "us" : "ca"]
    : ALL_REGIONS;

  return (
    <DropdownMenu>
      <Global styles={styles.dropdownMenuContent} />
      <DropdownMenu.Button aria-label={regionLoginPageLabel[currentRegion]}>
        {regionLabel(currentRegion)}
      </DropdownMenu.Button>
      <DropdownMenu.Content align="end">
        {regions.map(region =>
          region === currentRegion ? (
            <DropdownMenu.Item
              key={region}
              icon={faCheck}
              aria-current="true"
              aria-label={regionLoginPageLabel[region]}
            >
              {regionLabel(region)}
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item
              key={region}
              aria-label={goToRegionLabel[region]}
              href={regionLoginHref(region, slug)}
            >
              {regionLabel(region)}
            </DropdownMenu.Item>
          )
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export { CountrySelect };
export type { Props as CountrySelectProps };
